import { useState, useEffect, memo } from 'react';
import { CmsImage, globals, Icon, useScreenLayout } from 'common-ui';
import { css, useTheme } from '@emotion/react';
import type {
  ColourField,
  ImageEntryType,
  ProductCardEntryType,
  ProductProps,
  ProductPropsTransformed,
  RichTextEntryType,
} from 'tsconfig/types';
import { productLinkBuilder } from 'common-next/src/components/common-functions';
import { ProductCardSkeleton } from '../Skeleton/ProductCard/ProductCard';
import { useDisclosure } from '@mantine/hooks';
import Zoom from '../ItemCard/Zoom';

import ProductInfoDefault from './ProductInfoDefault';
import ProductInfoDetailed from './ProductInfoDetailed';
import ProductInfoMultiView from './ProductInfoMultiView';
import DefaultImage from './DefaultImage';
import { getProductCardStyle } from './ProductCard.utils';

export interface ProductCardPriceTypes {
  productPrice?: number | null;
  productPriceOriginal?: number | null;
  productMemberPrice?: number | null;
  productNumberOfBottles?: number | null;
}

export interface ProductCardProps {
  productName: string;
  productRating?: number;
  productReviewCount?: number;
  productPrice?: number | null;
  productPriceOriginal?: number | null;
  productImage?: string;
  productVintage?: string;
  productCardBackgroundImage?: ImageEntryType;
  productCardBackgroundColor?: ColourField;
  productCardButton?: string;
  productCode: string;
  productCardVariant?: string;
  productType?: string;
  productPricePrefix?: string;
  productNumberOfBottles?: number | null;
  productMemberPrice?: number | null;
  productCardTitle?: string;
  productCardTitleUrl?: string;
  productCardSubTitle?: string;
  productDescription?: string;
  productCardDescription?: RichTextEntryType | null;
  productImageOverride?: ImageEntryType;
  productImageSecondary?: ImageEntryType;
  productLink?: ProductCardEntryType['fields']['productLink'];
  multiViewPrices?: Array<ProductCardPriceTypes>;
  multiViewPriceDiff: boolean;
  isLoading?: boolean;
  hidePriceOnMultiView?: boolean;
  product?: ProductProps;
}

export const ProductCard = memo((props: ProductCardProps) => {
  const theme = useTheme();
  const { screen, isMobile } = useScreenLayout();

  const productCardVariant = props?.productCardVariant;
  const isLoading = props?.isLoading;

  const [imageURL, setImageURL] = useState('');
  const [opened, { open, close }] = useDisclosure(false);

  const largeProductMultiView = productCardVariant === 'Large Product Multi View';
  const fullBleedBackground = productCardVariant === 'Full bleed background';
  const standardProductCard = productCardVariant === 'Standard product card';

  const getOverrideImage = () => {
    const hasSmallImage = Boolean(props.productImageOverride?.fields?.smallDeviceImage?.fields?.file?.url);
    const smallImageUrl = props.productImageOverride?.fields?.smallDeviceImage?.fields?.file?.url;
    const largeImageUrl = props.productImageOverride?.fields?.largeDeviceImage?.fields?.file?.url;

    if (screen && isMobile && hasSmallImage) {
      return smallImageUrl;
    }

    return largeImageUrl;
  };

  const buildProductImageURL = (productCode: string): string =>
    `${globals.publicHost}/images/${globals.country}/en/product/${productCode}.png`;

  const getImageURL = () => {
    const overrideURL = getOverrideImage();
    if (overrideURL) {
      return overrideURL;
    }
    return buildProductImageURL(props.productCode);
  };

  const productData = () => {
    const {
      productName = '',
      productVintage = '',
      productCode = '',
      productRating = 0,
      productType = '',
      productPrice = 0,
    } = props;

    return {
      productName,
      vintage: productVintage,
      itemCode: productCode,
      giftFlag: false,
      avgRating: productRating,
      skus: [],
      packaging: '',
      productType,
      productWebType: '',
      largeImage: '',
      price: productPrice,
      buyersRRP: productPrice,
    };
  };

  const renderFullBleedBackgroundImage = (color: string, image?: any, isCase?: boolean) => (
    <div className="product-image-wrapper">
      <div className="product-background-image" style={{ backgroundColor: color }}>
        {image ? <CmsImage contentItem={image} /> : <DefaultImage imageURL={imageURL} isCase={isCase} />}
      </div>
    </div>
  );

  const renderStandardProductImage = (
    color: string,
    productName: string,
    vintage: string,
    code: string,
    isCase?: boolean,
  ) => (
    <>
      <a
        className="product-image-wrapper"
        href={globals.brandId === 'amx' ? undefined : productLinkBuilder(productName, vintage, code)}
        style={{ backgroundColor: color }}
      >
        <DefaultImage imageURL={imageURL} isCase={isCase} largeProductMultiView={largeProductMultiView} />
      </a>
      {standardProductCard && (
        <>
          <span className="zoom" onClick={open} onKeyDown={open} role="button" tabIndex={0}>
            <Icon kind="zoom" size="xs" />
          </span>
          <Zoom product={productData() as ProductPropsTransformed} opened={opened} handleOnClose={close} />
        </>
      )}
    </>
  );

  const renderProductImage = () => {
    const {
      productCardBackgroundColor,
      productCardBackgroundImage,
      productName,
      productVintage,
      productCode,
      productType,
    } = props;
    const backgroundColor = productCardBackgroundColor?.value ?? '';
    const isCase = productType === 'Case';

    if (fullBleedBackground) {
      return renderFullBleedBackgroundImage(backgroundColor, productCardBackgroundImage, isCase);
    }

    return renderStandardProductImage(backgroundColor, productName, productVintage as string, productCode, isCase);
  };

  const getProductInfoComponent = () => {
    if (productCardVariant === 'Detailed product card') {
      return ProductInfoDetailed;
    }
    if (largeProductMultiView) {
      return ProductInfoMultiView;
    }
    return ProductInfoDefault;
  };

  const renderProductInfo = () => {
    const ProductInfoComponent = getProductInfoComponent();
    return <ProductInfoComponent {...props} />;
  };

  useEffect(() => {
    setImageURL(getImageURL());
  }, [props.productImage, productCardVariant, props.productImageOverride, props.productCode, screen, isMobile]);

  if (isLoading) {
    return <ProductCardSkeleton />;
  }

  const productCardStyle = getProductCardStyle(props);
  const productCard = css`
    ${productCardStyle}
  `;

  const productCardImageStyles = largeProductMultiView ? theme.widgets.Product?.productCardImageMultiView : undefined;

  const productCardImage = css`
    ${productCardImageStyles}
  `;

  return (
    <div css={productCard} className="CmsProductCard">
      <div className="product-card" data-testid="product-card">
        <div css={productCardImage} className="product-image-and-info">
          {renderProductImage()}
          {renderProductInfo()}
        </div>
      </div>
    </div>
  );
});
